import { ChangeDetectionStrategy, Component, ViewChild, input, signal } from '@angular/core';
import { IconButtonComponent } from 'src/app/ui-kit/icon-button/icon-button.component';
import { ResponsiveModalComponent } from 'src/app/ui-kit/responsive-modal/responsive-modal.component';
import { HelpContentComponent } from './help-content.component';

/**
 * Help modal. Renders a small icon-button trigger that opens help content in a
 * `app-responsive-modal` — a bottom sheet on mobile, a centred dialog on
 * desktop. The modal header shows `title`; `subtitle` and any Vimeo video
 * render inside the scrollable body above the projected content.
 *
 * @example
 * <app-help-modal title="How scoring works" subtitle="A quick primer" icon="help-circle-outline">
 *   <p>Body content…</p>
 * </app-help-modal>
 *
 * @feature shared
 */
@Component({
  selector: 'app-help-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconButtonComponent, ResponsiveModalComponent, HelpContentComponent],
  host: { class: 'inline-flex' },
  template: `
    <app-icon-button
      [icon]="icon()"
      [ariaLabel]="ariaLabel()"
      color="info"
      size="xxs"
      (click)="open()"
    />

    <app-responsive-modal #modal [title]="title() ?? ''" (onDismiss)="isOpen.set(false)">
      <app-help-content [subtitle]="subtitle()" [videoUrl]="videoUrl()" [active]="isOpen()">
        <ng-content />
      </app-help-content>
    </app-responsive-modal>
  `,
})
export class HelpModalComponent {
  @ViewChild('modal') private modal!: ResponsiveModalComponent;

  /** Optional heading shown in the modal header. */
  readonly title = input<string>();

  /** Optional secondary line shown at the top of the body. */
  readonly subtitle = input<string>();

  /** Optional Vimeo reference rendered as a player above the body. */
  readonly videoUrl = input<string>();

  /** Trigger icon filename from `assets/icons/`. @default 'help-circle-outline' */
  readonly icon = input<string>('help-circle-outline');

  /** Accessible label for the icon-only trigger. @default 'Help' */
  readonly ariaLabel = input<string>('Help');

  /** Whether the modal is currently open. Gates the Vimeo iframe. */
  readonly isOpen = signal(false);

  open(): void {
    this.isOpen.set(true);
    this.modal.open();
  }
}
